import { useState, useRef, useSyncExternalStore } from 'react'
import { useAutoRetry } from '../../hooks/useAutoRetry'
import ErrorRetryBar from '../../components/ErrorRetryBar'
import { useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Avatar, Button, Input, Badge, Text, Checkbox, Spinner, Tooltip,
  Table, TableHeader, TableRow, TableHeaderCell,
  TableBody, TableCell,
  Dialog, DialogSurface, DialogTitle, DialogBody,
  DialogContent, DialogActions,
  MessageBar, MessageBarBody,
  Menu, MenuTrigger, MenuPopover, MenuList, MenuItem, MenuDivider,
  makeStyles, tokens,
} from '@fluentui/react-components'
import {
  AddRegular, SearchRegular, BuildingRegular,
  ArrowDownload24Regular, ArrowUpload24Regular,
  CheckmarkCircle24Regular, DismissCircle24Regular, Warning24Regular,
  LinkRegular, LinkDismissRegular, FolderLinkRegular,
  ChevronDownRegular, EditRegular,
} from '@fluentui/react-icons'
import { contractorsService } from '../../services/contractorsService'
import { exportsService } from '../../services/exportsService'
import { importsService, ImportResult } from '../../services/importsService'
import { configService, CONFIG_KEYS } from '../../services/configService'
import { useAuth } from '../../auth/useAuth'

function useIsMobile(breakpoint = 640) {
  return useSyncExternalStore(
    cb => { window.addEventListener('resize', cb); return () => window.removeEventListener('resize', cb) },
    () => window.innerWidth <= breakpoint,
    () => false,
  )
}

const useStyles = makeStyles({
  root:       { display: 'flex', flexDirection: 'column', gap: '16px', height: '100%', minHeight: 0 },
  header:     { display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' },
  spacer:     { flex: 1 },
  toolbar:    { display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '4px' },
  pagination: { display: 'flex', gap: '8px', justifyContent: 'flex-end', alignItems: 'center' },
  row:        { cursor: 'pointer', '&:hover': { backgroundColor: tokens.colorNeutralBackground1Hover } },
  // hlavičky
  th: {
    fontSize: '11px',
    fontWeight: '600',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.04em',
    color: tokens.colorNeutralForeground3,
  },
  // buňka firma
  nameCell:   { display: 'flex', alignItems: 'center', gap: '8px' },
  nameLines:  { display: 'flex', flexDirection: 'column' as const, minWidth: 0 },
  nameSub:    { fontSize: '11px', color: tokens.colorNeutralForeground3, whiteSpace: 'nowrap' as const, overflow: 'hidden', textOverflow: 'ellipsis' },
  // buňka kontakt
  contactCell:  { display: 'flex', flexDirection: 'column' as const, gap: '1px', minWidth: 0 },
  emailLink:    { fontSize: '13px', color: tokens.colorBrandForeground1, textDecoration: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const, display: 'block', '&:hover': { textDecoration: 'underline' } },
  phoneText:    { fontSize: '11px', color: tokens.colorNeutralForeground3 },
  importRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px',
    padding: '6px 0',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  importMessages: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '2px',
    fontSize: '12px',
  },
})

export default function ContractorsPage() {
  const styles = useStyles()
  const isMobile = useIsMobile()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const { isHR, isAdmin } = useAuth()
  const canEdit = isHR || isAdmin

  const [search, setSearch] = useState('')
  const [managerSearch, setManagerSearch] = useState('')
  const [showInactive, setShowInactive] = useState(false)
  const [page, setPage] = useState(1)
  const [isExporting, setIsExporting] = useState(false)
  const [isImporting, setIsImporting] = useState(false)
  const [importResult, setImportResult] = useState<ImportResult | null>(null)
  const [importError, setImportError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const { data: configData } = useQuery({
    queryKey: ['config'],
    queryFn: configService.getAll,
    staleTime: 5 * 60_000,
  })

  const getSpUrl = (isActive: boolean): string => {
    const get = (key: string) => configData?.find((c: any) => c.key === key)?.value ?? ''
    return isActive
      ? get(CONFIG_KEYS.SharePointUrl_ContractorActive)
      : get(CONFIG_KEYS.SharePointUrl_ContractorInactive)
  }

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['contractors', search, managerSearch, showInactive, page],
    queryFn: () => contractorsService.getAll({
      search: search || undefined,
      managerSearch: managerSearch || undefined,
      isActive: showInactive ? undefined : true,
      page,
      pageSize: 200,
    }),
  })

  const { autoRetryCount, autoRetryCountdown, maxRetries, resetAndRetry } = useAutoRetry(isError, refetch)

  const handleExport = async () => {
    setIsExporting(true)
    try { await exportsService.downloadContractors() }
    finally { setIsExporting(false) }
  }

  const handleImportClick = () => fileInputRef.current?.click()

  const handleFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    e.target.value = ''
    setIsImporting(true)
    setImportError(null)
    setImportResult(null)
    try {
      const result = await importsService.importContractors(file)
      setImportResult(result)
      qc.invalidateQueries({ queryKey: ['contractors'] })
    } catch (err: any) {
      setImportError(err?.response?.data?.detail ?? err?.message ?? 'Chyba při importu.')
    } finally {
      setIsImporting(false)
    }
  }

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <Text size={600} weight="semibold">
          <BuildingRegular style={{ verticalAlign: 'middle', marginRight: 8 }} />
          Externisté
        </Text>
        <div className={styles.spacer} />
        {getSpUrl(true) && (
          <Tooltip content="Otevřít SharePoint složku" relationship="label">
            <Button appearance="subtle" icon={<FolderLinkRegular />}
              onClick={() => window.open(getSpUrl(true), '_blank', 'noopener')}>
              SharePoint
            </Button>
          </Tooltip>
        )}
        {canEdit && (
          <>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx"
              style={{ display: 'none' }}
              onChange={handleFileSelected}
            />
            <Menu>
              <MenuTrigger disableButtonEnhancement>
                <Button icon={<ChevronDownRegular />} iconPosition="after">
                  Export &amp; Import
                </Button>
              </MenuTrigger>
              <MenuPopover>
                <MenuList>
                  <MenuItem
                    icon={isExporting ? <Spinner size="tiny" /> : <ArrowDownload24Regular />}
                    onClick={handleExport}
                    disabled={isExporting}
                  >
                    Export externistů (Excel)
                  </MenuItem>
                  <MenuDivider />
                  <MenuItem
                    icon={isImporting ? <Spinner size="tiny" /> : <ArrowUpload24Regular />}
                    onClick={handleImportClick}
                    disabled={isImporting}
                  >
                    Import externistů
                  </MenuItem>
                </MenuList>
              </MenuPopover>
            </Menu>
            <Button appearance="primary" icon={<AddRegular />} onClick={() => navigate('/contractors/new')}>
              Přidat externistu
            </Button>
          </>
        )}
      </div>

      {importError && (
        <MessageBar intent="error">
          <MessageBarBody>{importError}</MessageBarBody>
        </MessageBar>
      )}

      <div className={styles.toolbar}>
        <Input
          style={{ minWidth: 280 }}
          placeholder="Hledat externistu..."
          contentBefore={<SearchRegular />}
          value={search}
          onChange={(_, d) => { setSearch(d.value); setPage(1) }}
        />
        <Input
          style={{ minWidth: 280 }}
          placeholder="Hledat dle manažera..."
          contentBefore={<SearchRegular />}
          value={managerSearch}
          onChange={(_, d) => { setManagerSearch(d.value); setPage(1) }}
        />
        <Checkbox
          label="Zobrazit neaktivní"
          checked={showInactive}
          onChange={(_, d) => { setShowInactive(!!d.checked); setPage(1) }}
        />
        <div className={styles.spacer} />
        {data && (
          <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
            Celkem: {data.totalCount}
          </Text>
        )}
      </div>

      <ErrorRetryBar
        isError={isError}
        message="Nepodařilo se načíst externisty."
        autoRetryCount={autoRetryCount}
        autoRetryCountdown={autoRetryCountdown}
        maxRetries={maxRetries}
        onRetry={resetAndRetry}
      />

      {isLoading && <Spinner label="Načítání..." />}

      <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
      <Table style={{ tableLayout: 'fixed', width: '100%' }}>
        <TableHeader>
          <TableRow className="sticky-head">
            <TableHeaderCell style={{ width: isMobile ? '55%' : '28%' }}><span className={styles.th}>Firma</span></TableHeaderCell>
            {!isMobile && <TableHeaderCell style={{ width: '22%' }}><span className={styles.th}>E-mail / Mobil</span></TableHeaderCell>}
            <TableHeaderCell style={{ width: isMobile ? '30%' : '16%' }}><span className={styles.th}>Odpovídá / Tým</span></TableHeaderCell>
            {!isMobile && <TableHeaderCell style={{ width: 130 }}><span className={styles.th}>IČO / Zahájení</span></TableHeaderCell>}
            <TableHeaderCell style={{ width: isMobile ? '15%' : 90 }}><span className={styles.th}>Stav</span></TableHeaderCell>
            {canEdit && !isMobile && <TableHeaderCell style={{ width: 72 }}><span className={styles.th}></span></TableHeaderCell>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {data?.items.map(c => {
            const contactFull = `${c.contactFirstName} ${c.contactLastName}`.trim()
            const nameLower = c.companyName.toLowerCase()
            const nameInCompany = nameLower.includes(c.contactFirstName.toLowerCase())
              && nameLower.includes(c.contactLastName.toLowerCase())
            const displayName = nameInCompany ? contactFull : `${contactFull} (${c.companyName})`
            const emailForContact = c.orbitEmail || c.contactEmail
            return (
            <TableRow key={c.id} className={styles.row} onClick={() => navigate(`/contractors/${c.id}`)}>
              {/* Firma + činnosti */}
              <TableCell>
                <div className={styles.nameCell}>
                  <Avatar size={32} name={c.companyName} icon={<BuildingRegular />} />
                  <div className={styles.nameLines}>
                    <Text weight="semibold" size={300}>{displayName}</Text>
                    {c.expectedActivities && (
                      <span className={styles.nameSub}>{c.expectedActivities}</span>
                    )}
                  </div>
                </div>
              </TableCell>
              {/* ORBIT e-mail + mobil */}
              {!isMobile && (
                <TableCell onClick={e => e.stopPropagation()}>
                  <div className={styles.contactCell}>
                    {emailForContact && (
                      <a href={`mailto:${emailForContact}`} className={styles.emailLink}>
                        {emailForContact}
                      </a>
                    )}
                    {c.phone && (
                      <span className={styles.phoneText}>{c.phone}</span>
                    )}
                  </div>
                </TableCell>
              )}
              {/* Odpovídá / Tým */}
              <TableCell>
                <Text size={300}>{c.managerFullName ?? '—'}</Text>
                {c.departmentName && (
                  <span style={{ display: 'block', fontSize: '11px', color: tokens.colorNeutralForeground3, marginTop: 1 }}>
                    {c.departmentName}
                  </span>
                )}
              </TableCell>
              {/* IČO / zahájení spolupráce */}
              {!isMobile && (
                <TableCell>
                  <Text size={300}>{c.ico}</Text>
                  {c.cooperationStartDate && (
                    <span style={{ display: 'block', fontSize: 12, color: tokens.colorNeutralForeground3, marginTop: 2 }}>
                      {new Date(c.cooperationStartDate).toLocaleDateString('cs-CZ', { day: 'numeric', month: 'numeric', year: 'numeric' })}
                    </span>
                  )}
                </TableCell>
              )}
              {/* Stav */}
              <TableCell>
                <Badge appearance="filled" color={c.isActive ? 'success' : 'danger'}>
                  {c.isActive ? 'Aktivní' : 'Neaktivní'}
                </Badge>
              </TableCell>
              {/* ORBIT e-mail ikona + Edit */}
              {canEdit && !isMobile && (
                <TableCell onClick={e => e.stopPropagation()} style={{ textAlign: 'right' }}>
                  <Tooltip
                    content={c.orbitEmail ? `ORBIT e-mail: ${c.orbitEmail}` : 'Nemá ORBIT e-mail'}
                    relationship="label"
                  >
                    <span style={{ verticalAlign: 'middle', marginRight: 4 }}>
                      {c.orbitEmail
                        ? <LinkRegular style={{ color: tokens.colorPaletteGreenForeground1, fontSize: 16 }} />
                        : <LinkDismissRegular style={{ color: tokens.colorPaletteRedForeground1, fontSize: 16 }} />
                      }
                    </span>
                  </Tooltip>
                  <Button size="small" appearance="subtle" icon={<EditRegular />}
                    onClick={() => navigate(`/contractors/${c.id}/edit`)} />
                </TableCell>
              )}
            </TableRow>
          )})}
          {!isLoading && data?.items.length === 0 && (
            <TableRow><TableCell colSpan={6}>Žádné záznamy.</TableCell></TableRow>
          )}
        </TableBody>
      </Table>
      </div>

      <div className={styles.pagination}>
        <Button disabled={page <= 1} onClick={() => setPage(p => p - 1)}>‹</Button>
        <span>Strana {page} / {Math.max(1, Math.ceil((data?.totalCount ?? 0) / 200))}</span>
        <Button disabled={(data?.totalCount ?? 0) <= page * 200} onClick={() => setPage(p => p + 1)}>›</Button>
      </div>

      {/* Dialog s výsledky importu */}
      <Dialog open={importResult !== null} onOpenChange={() => setImportResult(null)}>
        <DialogSurface style={{ maxWidth: 640 }}>
          <DialogTitle>Výsledek importu externistů</DialogTitle>
          <DialogBody>
            <DialogContent>
              {importResult && (
                <>
                  <div style={{ display: 'flex', gap: 24, marginBottom: 16 }}>
                    <Text>
                      <CheckmarkCircle24Regular style={{ color: tokens.colorPaletteGreenForeground1, verticalAlign: 'middle' }} />{' '}
                      Vytvořeno: <strong>{importResult.created}</strong>
                    </Text>
                    <Text>
                      <CheckmarkCircle24Regular style={{ color: tokens.colorBrandForeground1, verticalAlign: 'middle' }} />{' '}
                      Aktualizováno: <strong>{importResult.updated}</strong>
                    </Text>
                    <Text>
                      <DismissCircle24Regular style={{ color: tokens.colorPaletteRedForeground1, verticalAlign: 'middle' }} />{' '}
                      Přeskočeno: <strong>{importResult.skipped}</strong>
                    </Text>
                  </div>

                  {importResult.rows.filter(r => r.messages.length > 0 || r.action === 'skipped').length > 0 ? (
                    <div style={{ maxHeight: 360, overflowY: 'auto' }}>
                      {importResult.rows
                        .filter(r => r.messages.length > 0 || r.action === 'skipped')
                        .map(row => (
                          <div key={row.rowNumber} className={styles.importRow}>
                            <div style={{ flexShrink: 0, marginTop: 2 }}>
                              {row.action === 'skipped'
                                ? <DismissCircle24Regular style={{ color: tokens.colorPaletteRedForeground1 }} />
                                : <Warning24Regular style={{ color: tokens.colorPaletteYellowForeground1 }} />
                              }
                            </div>
                            <div>
                              <Text weight="semibold" size={200}>Řádek {row.rowNumber} — {row.name}</Text>
                              <div className={styles.importMessages}>
                                {row.messages.map((msg, i) => (
                                  <Text key={i} size={100} style={{
                                    color: msg.type === 'error'
                                      ? tokens.colorPaletteRedForeground1
                                      : tokens.colorPaletteYellowForeground1,
                                  }}>
                                    {msg.type === 'error' ? '❌' : '⚠️'} {msg.text}
                                  </Text>
                                ))}
                              </div>
                            </div>
                          </div>
                        ))}
                    </div>
                  ) : (
                    <Text>Všechny záznamy byly úspěšně zpracovány bez upozornění.</Text>
                  )}
                </>
              )}
            </DialogContent>
            <DialogActions>
              <Button appearance="primary" onClick={() => setImportResult(null)}>Zavřít</Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  )
}
